import { ChangeEvent, MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Box, Checkbox, FormControlLabel, IconButton, TextField, Typography } from '@mui/material';
import { Styles } from 'common/types/styles';
import { FieldLabelWrapper } from 'common/ui/inputs/field-label-wrapper';
import { LightTooltip } from 'common/ui/containers/light-tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import { SelectField, SelectFieldOption } from 'common/ui/inputs/select-field';
import { ActionsOption, ActionsPopover } from 'common/ui/inputs/actions-popover';
import { AIModelsSelect } from 'common/ui/inputs/ai-models-select';
import { ButtonWithLoading } from 'common/ui/containers/button-with-loading';
import { userService } from 'common/services/user.service';
import { PDPType, TemplateRuleSettings } from 'common/types/common';
import { format } from 'date-fns';
import {
  useCreatePromptMutation,
  useNormalizationConfigMutation,
  useSelectAttributeModelMutation,
  useSelectModelMutation,
  useSelectVersionMutation,
} from 'common/hooks/api/mutations/use-ai-prompts-mutation';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQueryClient } from '@tanstack/react-query';
import {
  AIPromptsQueryKey,
  useListPromptsQuery,
  useSelectedModelQuery,
  useSelectedPromptVersionQuery,
  useTemplateValuesQuery,
} from 'common/hooks/api/queries/use-ai-prompts-query';
import { useRetailerContext } from 'common/ui/inputs/retailer/retailer-ctx';
import { CategoryQueryKey } from 'common/hooks/api/queries/use-category-query';
import { RetailersTitleMap } from 'common/constants/entities';
import { ProductAITabValue } from './constants';

const getPromptType = (value: string) => {
  switch (value) {
    case 'system':
      return 2;
    default:
      return 1;
  }
};

const styles: Styles = {
  container: { display: 'flex', flexDirection: 'column', gap: 2, mb: 2, pb: 2, borderBottom: '1px solid #7f82992a' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' },
  modelSelect: { flexGrow: 1 },
  templateSelect: { width: 250 },
  typeSelect: { width: 250 },
  templateSelectContainer: { display: 'flex', alignItems: 'center', gap: 0.5 },
  btn: { flexGrow: 1 },
  label: { color: '#7F8299', fontSize: 12 },
  labelWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  saveAttr: {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
  },
};

interface Props {
  selectedTab: ProductAITabValue;
  attributeKey?: string;
  attributeID?: string;
  attributeSettings?: TemplateRuleSettings;
  pid?: string;
  type: PDPType;
  config?: boolean;
  configType?: string;
  configAvailable?: boolean;
  configLoading?: boolean;
  hideTitle?: boolean;
}

export function TemplateType({
  selectedTab,
  attributeKey,
  attributeID,
  attributeSettings,
  pid,
  type,
  config,
  configType,
  configAvailable,
  configLoading,
  hideTitle,
}: Props): ReactElement {
  const [prompt, setPrompt] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [placeholdersAnchorEl, setPlaceholdersAnchorEl] = useState<HTMLButtonElement>();
  const [selectedTemplateID, setSelectedTemplateID] = useState<number | string>();
  const [defaultTemplateID, setDefaultTemplateID] = useState<number | string>();
  const [configValue, setConfigValue] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { data: placeholders, isFetching: isPlaceholdersFetching } = useTemplateValuesQuery({
    generation_type: type?.value,
    product_id: pid ?? null,
    ...(type?.value === 1 ? { attribute_name: attributeKey } : {}),
    ...(userService.getSubRetailer() ? { sub_vendor_id: userService.getSubRetailer() } : {}),
  });

  const { data: selectedModelData, isFetching: isSelectedModelDataFetching } = useSelectedModelQuery({
    generation_type: type?.value,
    ...(userService.getSubRetailer() ? { sub_vendor_id: userService.getSubRetailer() } : {}),
  });

  const { retailer } = useRetailerContext();

  const {
    data: templatesData,
    isFetched: isFetchedTemplates,
    refetch,
  } = useListPromptsQuery({
    generation_type: type?.value,
    prompt_type: getPromptType(type.promptType),
    ...(userService.getSubRetailer() ? { sub_vendor_id: userService.getSubRetailer() } : {}),
  });

  const {
    data: selectedTemplatesData,
    isFetched: isFetchedSelectedTemplate,
    refetch: refetchSelectedTemplate,
  } = useSelectedPromptVersionQuery({
    generation_type: type?.value,
    prompt_type: getPromptType(type.promptType),
    ...(userService.getSubRetailer() ? { sub_vendor_id: userService.getSubRetailer() } : {}),
  });

  const { mutate: mutateSavePromptTemplate, isLoading: isSavePromptTemplateLoading } = useCreatePromptMutation();
  const { mutate: mutateDefaultTemplate } = useSelectVersionMutation();
  const { mutate: mutateSaveModel, isLoading: isSaveModelLoading } = useSelectModelMutation();
  const { mutate: onSelectAttributeModel, isLoading: isSelectModelLoading } = useSelectAttributeModelMutation();
  const { mutate: onChangeConfig, isLoading: isConfigLoading } = useNormalizationConfigMutation();

  const placeholdersOptions: Array<ActionsOption> = useMemo(() => {
    return Object.keys(placeholders ?? {}).map(key => ({
      name: key,
      value: key,
    }));
  }, [placeholders]);

  const templatesOptions: Array<SelectFieldOption> = useMemo(() => {
    return (templatesData?.items ?? []).map(t => ({
      label: `ver.${t?.version} ${format(new Date(t?.created_at), 'yyyy-MM-dd')}`,
      value: t.id,
    }));
  }, [templatesData?.items]);

  const handlePlaceholdersOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setPlaceholdersAnchorEl(e.currentTarget);
  };

  const handlePlaceholdersClose = () => {
    setPlaceholdersAnchorEl(null);
  };

  const handleTemplateChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTemplate(e.target.value);
  };

  const handleSelectModel = (value: string) => {
    setSelectedModel(value);
  };

  const handleSelectedModelChange = (value: string) => {
    handleSelectModel(value);
  };

  const handleTemplateSave = () => {
    mutateSavePromptTemplate(
      {
        prompt: template,
        generationType: type?.value,
        promptType: getPromptType(type.promptType),
        ...(userService.getSubRetailer() ? { subVendorId: userService.getSubRetailer() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`${type?.displayName} template saved succesfully`);
          refetch();
          refetchSelectedTemplate();
        },
      }
    );
  };

  const handleInvalidateQueries = () => {
    [AIPromptsQueryKey.SelectedModel, AIPromptsQueryKey.ListPrompts, CategoryQueryKey.Category].forEach(q =>
      queryClient.invalidateQueries([q])
    );
  };

  const handleSetDefaultTemplate = () => {
    if (selectedTemplateID === defaultTemplateID || selectedTemplateID === 'default') return;

    setDefaultTemplateID(selectedTemplateID);
    mutateDefaultTemplate(
      {
        id: selectedTemplateID as number,
        generationType: type?.value,
        promptType: getPromptType(type.promptType),
      },
      {
        onSuccess: () => {
          toast.success(`${type?.displayName} default template changed succesfully`);
          refetchSelectedTemplate();
        },
      }
    );
  };

  const handleModelSave = () => {
    mutateSaveModel(
      {
        model: selectedModel,
        generationType: type?.value,
        ...(userService.getSubRetailer() ? { subVendorId: userService.getSubRetailer() } : {}),
      },
      {
        onSuccess: () => {
          toast.success('AI Model saved succesfully');
          handleInvalidateQueries();
        },
      }
    );
  };

  const handleModelAttrSave = (model: string) => {
    onSelectAttributeModel(
      {
        model,
        attributeID,
      },
      {
        onSuccess: () => {
          toast.success('AI Model saved succesfully');
          handleInvalidateQueries();
        },
      }
    );
  };

  const handleConfigChange = () => {
    onChangeConfig(
      {
        vendor: RetailersTitleMap[retailer],
        [configType]: !configValue,
      },
      {
        onSuccess: () => {
          setConfigValue(!configValue);
        },
      }
    );
  };

  useEffect(() => {
    if (!isFetchedTemplates || !isFetchedSelectedTemplate) return;

    const selectedTemplate = templatesOptions?.find(item => item?.value === selectedTemplatesData?.selected?.id)
      ? selectedTemplatesData?.selected?.id
      : null;

    const templatesDataLastIndex = templatesData?.items?.length ? templatesData.items.length - 1 : 0;
    const defaultTemplate = templatesData?.items?.[templatesDataLastIndex]?.id;
    setSelectedTemplateID(selectedTemplate ?? defaultTemplate ?? 'default');
  }, [
    isFetchedSelectedTemplate,
    isFetchedTemplates,
    selectedTemplatesData?.selected?.id,
    templatesData?.items,
    templatesOptions,
  ]);

  useEffect(() => {
    if (!selectedTemplateID) return;

    const defaultTemplate = templatesData?.items?.[0]?.prompt;

    if (selectedTemplateID === 'default') setTemplate(defaultTemplate);
    else setTemplate((templatesData?.items ?? []).find(t => t.id === selectedTemplateID)?.prompt || '');
  }, [selectedTemplateID, templatesData?.items]);

  useEffect(() => {
    if (!placeholders) return;

    let prompt = template;

    Object.entries(placeholders).forEach(([key, value]) => {
      prompt = prompt?.replace(new RegExp(`{${key}}`, 'g'), value);
    });

    setPrompt(prompt);
  }, [template, placeholders]);

  useEffect(() => {
    if (!isFetchedTemplates || !isFetchedSelectedTemplate) return;
    setDefaultTemplateID(selectedTemplatesData?.selected?.id);
  }, [isFetchedSelectedTemplate, isFetchedTemplates, selectedTemplatesData]);

  useEffect(() => {
    if (!selectedModelData?.selected?.model || isSelectedModelDataFetching) return;
    if (type?.value === 1 && attributeID) {
      // attribute_generation
      setSelectedModel(attributeSettings?.model_name);
    } else {
      setSelectedModel(selectedModelData?.selected?.model);
    }
  }, [
    selectedModelData?.selected,
    isSelectedModelDataFetching,
    attributeSettings?.model_name,
    type?.value,
    attributeID,
  ]);

  useEffect(() => {
    if (configAvailable && !configLoading) {
      setConfigValue(config);
    }
  }, [config, configAvailable, configLoading]);

  return (
    <>
      <Box sx={styles.container}>
        {!hideTitle && <Typography>{type?.displayName}</Typography>}
        <Box sx={styles.head}>
          {selectedTemplateID && isFetchedTemplates && (
            <FieldLabelWrapper label="Template Version">
              <Box sx={styles.templateSelectContainer}>
                <SelectField
                  options={templatesOptions}
                  value={selectedTemplateID}
                  onChange={setSelectedTemplateID}
                  sx={styles.templateSelect}
                />
                {selectedTemplateID !== 'default' && (
                  <LightTooltip
                    title={selectedTemplateID === defaultTemplateID ? 'Default Template' : 'Set as Default'}
                    placement="right"
                  >
                    <IconButton onClick={handleSetDefaultTemplate}>
                      {selectedTemplateID === defaultTemplateID ? (
                        <CheckBoxIcon color="success" />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </IconButton>
                  </LightTooltip>
                )}
              </Box>
            </FieldLabelWrapper>
          )}

          {selectedTab === ProductAITabValue.Template && (
            <Box>
              <LightTooltip title="Copy Placeholder" placement="left">
                <IconButton onClick={handlePlaceholdersOpen}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </LightTooltip>
            </Box>
          )}
          {configAvailable && !configLoading && (
            <FormControlLabel
              control={<Checkbox checked={configValue} disabled={isConfigLoading} onChange={handleConfigChange} />}
              label={<Typography sx={styles.label}>Enabled</Typography>}
            />
          )}
        </Box>

        {[ProductAITabValue.Template, ProductAITabValue.Normalization].includes(selectedTab) && (
          <TextField
            value={template}
            onChange={handleTemplateChange}
            maxRows={6}
            minRows={6}
            InputLabelProps={{ shrink: true }}
            disabled={isPlaceholdersFetching}
            fullWidth
            multiline
          />
        )}

        {selectedTab === ProductAITabValue.Prompt && (
          <TextField value={prompt} maxRows={6} minRows={6} InputLabelProps={{ shrink: true }} fullWidth multiline />
        )}

        <Box component="span" sx={styles.btn}>
          <ButtonWithLoading
            variant="contained"
            color="primary"
            loading={isPlaceholdersFetching || isSavePromptTemplateLoading}
            onClick={handleTemplateSave}
            fullWidth
          >
            Save Template
          </ButtonWithLoading>
        </Box>

        {type.promptType !== 'system' && (
          <>
            <AIModelsSelect
              value={selectedModel}
              onChange={handleSelectedModelChange}
              sx={styles.modelSelect}
              label="AI Model"
              process="ai_optimization"
            />

            {type?.value === 1 && attributeID ? ( // attribute_generation
              <Box sx={styles.saveAttr}>
                <ButtonWithLoading
                  variant="contained"
                  color="primary"
                  loading={isSelectModelLoading || isSelectedModelDataFetching}
                  onClick={() => handleModelAttrSave(selectedModel)}
                  fullWidth
                >
                  Save Attribute Model Selection
                </ButtonWithLoading>

                <ButtonWithLoading
                  variant="outlined"
                  color="primary"
                  loading={isSelectModelLoading || isSelectedModelDataFetching}
                  onClick={() => handleModelAttrSave(null)}
                  fullWidth
                >
                  Reset Attribute Model Selection
                </ButtonWithLoading>
              </Box>
            ) : (
              <ButtonWithLoading
                variant="contained"
                color="primary"
                loading={isSaveModelLoading || isSelectedModelDataFetching}
                onClick={handleModelSave}
                fullWidth
              >
                Save AI Model
              </ButtonWithLoading>
            )}
          </>
        )}
      </Box>

      <ActionsPopover
        anchor={placeholdersAnchorEl}
        open={Boolean(placeholdersAnchorEl)}
        onClose={handlePlaceholdersClose}
        options={placeholdersOptions}
        onClick={value => {
          navigator.clipboard.writeText(`{${value}}`);
          toast.success('Placeholder copied!');
        }}
      />
    </>
  );
}
